import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { companyTenetKeys } from "@/lib/query";

export interface CompanyTenet {
  id: string;
  name: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * The company's tenets. `activeOnly` is what a reviewer sees when endorsing —
 * a retired tenet keeps resolving on endorsements already given, but is not
 * offered again.
 */
export function useCompanyTenets(activeOnly = false, enabled = true) {
  return useQuery({
    queryKey: activeOnly ? companyTenetKeys.active : companyTenetKeys.all,
    queryFn: async (): Promise<CompanyTenet[]> => {
      const res = await fetch(`/api/company-tenets${activeOnly ? "?active=true" : ""}`);
      if (!res.ok) throw new Error("Failed to fetch company tenets");
      return (await res.json()).data;
    },
    enabled,
  });
}

function invalidate(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: companyTenetKeys.all });
  queryClient.invalidateQueries({ queryKey: companyTenetKeys.active });
}

export interface CompanyTenetSyncResult {
  added: number;
  updated: number;
  retired: number;
  unchanged: number;
}

function describeSync({ added, updated, retired }: CompanyTenetSyncResult): string {
  const parts = [
    added > 0 && `${added} added`,
    updated > 0 && `${updated} updated`,
    retired > 0 && `${retired} retired`,
  ].filter((part): part is string => typeof part === "string");
  return parts.length > 0 ? `Tenets refreshed — ${parts.join(", ")}` : "Tenets already up to date";
}

/**
 * Pull the tenets from the legacy Feedback app. The only way the list
 * changes — Enable doesn't edit it.
 */
export function useSyncCompanyTenets() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<CompanyTenetSyncResult> => {
      const res = await fetch("/api/company-tenets/sync", { method: "POST" });
      const body = (await res.json().catch(() => null)) as {
        data?: CompanyTenetSyncResult;
        error?: string;
      } | null;
      if (!res.ok || !body?.data) throw new Error(body?.error ?? "Refresh failed");
      return body.data;
    },
    onSuccess: (result) => {
      invalidate(queryClient);
      toast.success(describeSync(result));
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
